import * as m from '@staiter/i18n';
import type { CogsReport, CogsReportRow } from '@staiter/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { PackageCheck } from 'lucide-react';
import { useMemo, useState } from 'react';
import { EmptyState, ExportMenu, PageHeader, Table, type TableColumn } from '~/components/data';
import { CogsTrendChart, HeroMetric } from '~/components/domain';
import { DateRangePicker, PeriodPresetChips } from '~/components/primitives';
import { fetchCogsReport } from '~/lib/api/finance';
import { formatMoney } from '~/lib/format';
import { VALUATION_METHOD_LABELS, labelOf } from '~/lib/i18n/labels';
import shared from './_auth._shared.module.css';
import styles from './_auth.finance.cogs.module.css';

/**
 * S13-FE.4 — COGS report page (period query + item rows + totals).
 */

export const Route = createFileRoute('/_auth/finance/cogs')({
  staticData: { minMainWidth: 960 },
  component: FinanceCogsPage,
});

function today(): string {
  return new Date().toISOString().slice(0, 10);
}
// S-FIX-6: default YTD (см. cash-flow для обоснования).
function firstOfYear(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-01-01`;
}

function FinanceCogsPage(): JSX.Element {
  const [dateFrom, setDateFrom] = useState(firstOfYear());
  const [dateTo, setDateTo] = useState(today());

  const query = useQuery({
    queryKey: ['finance', 'cogs', dateFrom, dateTo],
    queryFn: () => fetchCogsReport({ date_from: dateFrom, date_to: dateTo }),
    enabled: Boolean(dateFrom && dateTo),
  });

  const report: CogsReport | undefined = query.data;
  const rows = report?.rows ?? [];
  // FE-273 Pass 39: total cogs для share column (% от итого).
  const totalCogs = Number(report?.totals.cogs_amount.amount ?? 0);

  const columns: TableColumn<CogsReportRow>[] = useMemo(
    () => [
      {
        id: 'item',
        header: m.cogs_column_item(),
        // FE-99 honest-audit: drill-down click → /stock/ledger filtered by item_id.
        accessor: (r) => (
          <a
            href={`/stock/ledger?item=${encodeURIComponent(r.item_id ?? '')}`}
            style={{ color: 'var(--color-accent)', textDecoration: 'none' }}
            title="Открыть Журнал движений по этой позиции"
          >
            {`${r.item_sku} — ${r.item_name_ru}`}
          </a>
        ),
        sticky: true,
        sortable: true,
        sortValue: (r) => r.item_sku,
      },
      {
        id: 'qty',
        header: m.cogs_column_qty_sold(),
        accessor: (r) => formatMoney(r.qty_sold),
        align: 'right',
        mono: true,
        sortable: true,
        sortValue: (r) => Number(r.qty_sold.amount),
      },
      {
        id: 'amount',
        header: m.cogs_column_cogs_amount(),
        accessor: (r) => formatMoney(r.cogs_amount),
        align: 'right',
        mono: true,
        sortable: true,
        sortValue: (r) => Number(r.cogs_amount.amount),
      },
      // FE-273 Pass 39: «Доля» share column = row cogs / total cogs.
      // FE-99 / BE-11: clarification — share означает «от итого COGS периода»,
      // не «от выручки». Tooltip нужен чтобы accountant не путал.
      // Outlier detection — sort desc показывает топ-расходные позиции.
      {
        id: 'share',
        header: m.cogs_column_share(),
        // FE-99/BE-11 honest-audit: anomaly flag когда per-unit cost >> avg для категории
        // (signals UoM precision drift e.g. g vs kg). Также drill-down link → /stock/ledger.
        accessor: (r) => {
          if (totalCogs === 0) return '—';
          const pct = (Number(r.cogs_amount.amount) / totalCogs) * 100;
          const avgUnit = Number(r.avg_cost_per_unit.amount);
          const qty = Number(r.qty_sold ?? 0);
          // Anomaly: tiny qty but huge cogs share → potential UoM drift.
          const isAnomaly = qty > 0 && qty < 10 && pct > 10;
          return (
            <span
              title={
                isAnomaly
                  ? `⚠ Аномалия: при количестве ${qty} доля = ${pct.toFixed(1)}% (возможна ошибка UoM)`
                  : 'Доля от итого себестоимости периода (не от выручки)'
              }
              style={isAnomaly ? { color: 'var(--color-warn)', fontWeight: 600 } : undefined}
            >
              {isAnomaly && '▲ '}
              {pct.toFixed(1)}%
            </span>
          );
        },
        align: 'right',
        mono: true,
        sortable: true,
        sortValue: (r) => (totalCogs === 0 ? 0 : Number(r.cogs_amount.amount) / totalCogs),
        width: '110px',
      },
      {
        id: 'avg',
        header: m.cogs_column_avg_cost(),
        accessor: (r) => formatMoney(r.avg_cost_per_unit),
        align: 'right',
        mono: true,
        sortable: true,
        sortValue: (r) => Number(r.avg_cost_per_unit.amount),
      },
      {
        id: 'method',
        header: m.cogs_column_valuation(),
        // FE-231: localize raw enum через VALUATION_METHOD_LABELS.
        accessor: (r) =>
          labelOf(
            VALUATION_METHOD_LABELS,
            r.valuation_method as keyof typeof VALUATION_METHOD_LABELS,
          ),
        width: '160px',
      },
    ],
    [totalCogs],
  );

  return (
    <div>
      <PageHeader
        title={m.phase2_finance_cogs_title()}
        actions={
          // FE-153 Sprint N+ batch 3: Excel export CTA для period-close mirror.
          <ExportMenu resource="finance-cogs" filters={{ date_from: dateFrom, date_to: dateTo }} />
        }
      />

      <div className={styles.filterRow}>
        {/* S-FIX-5 fix: native `<input type=date>` запрещён (feedback_ui_native_selects) — DateRangePicker primitive. */}
        <DateRangePicker
          label={m.cogs_filter_date_from()}
          value={{ from: dateFrom || null, to: dateTo || null }}
          onChange={(r) => {
            setDateFrom(r.from ?? '');
            setDateTo(r.to ?? '');
          }}
        />
        {/* FE-279 Pass 27: shared preset chips. */}
        <PeriodPresetChips
          onChange={(r) => {
            setDateFrom(r.from);
            setDateTo(r.to);
          }}
        />
      </div>

      {rows.length === 0 && !query.isLoading ? (
        <EmptyState title={m.cogs_empty_title()} icon={<PackageCheck size={32} />} />
      ) : (
        <>
          {/* FE-154 Sprint Q+: hero metric ABOVE table (вместо мелкого totalsCard ниже). */}
          {report && (
            <div className={shared.heroRow}>
              {/* FE-176/268 Pass 52: ProvenanceChip — COGS computed в STAITER (cost-engine). */}
              <HeroMetric
                label={m.cogs_totals()}
                value={report.totals.cogs_amount}
                compact
                provenance={{ source: 'staiter', fetched_at: new Date().toISOString() }}
              />
              {/* FE-100: period scope chip — Hero self-contained, при экспорте/screenshot
                  виден период с числом. */}
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px',
                  marginLeft: 12,
                  background: 'var(--color-bg-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 999,
                  fontSize: 12,
                  color: 'var(--color-text-secondary)',
                  fontVariantNumeric: 'tabular-nums',
                  alignSelf: 'center',
                }}
              >
                {dateFrom} — {dateTo}
              </div>
            </div>
          )}
          <CogsTrendChart rows={rows} />
          <Table<CogsReportRow>
            data={rows}
            columns={columns}
            rowKey={(r) => r.item_id}
            ariaLabel={m.phase2_finance_cogs_title()}
            loading={query.isLoading}
            emptyTitle={m.cogs_empty_title()}
          />
          {report && (
            <div className={styles.totalsCard}>
              <strong>{m.cogs_totals()}:</strong>{' '}
              <span data-testid="cogs-total-qty">{formatMoney(report.totals.qty_sold)}</span>
              {' / '}
              <span data-testid="cogs-total-amount">{formatMoney(report.totals.cogs_amount)}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
